import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { LoadingState } from "@/components/base/LoadingState";
import { money } from "@/lib/format";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { isAdmin, canWorkSales, canManagePurchaseOrders, canOperateInventory, canManageProducts } from "@/lib/permissions";
import { firstRelation } from "@/lib/relations";
import { isTransientError, serializeError, attentionDegraded, describeDashboardError, dashboardQueryEnabled, dashboardWorkspaceSurface } from "@/lib/dashboard";
import { buildStockValuation, dashboardStockValueCard } from "@/lib/inventory-valuation";

interface ProductRow { id: string; name: string; sku: string; status: string; reorder_point: number; reorder_qty: number; cost_price: number | null; }
interface VariantRow { id: string; product_id: string; cost_price: number | null; }
interface BalanceRow { product_id: string; variant_id: string | null; location_id: string; on_hand: number; allocated: number; available: number; incoming: number; products?: { name: string; reorder_point: number; reorder_qty: number } | null; }
interface SoItemRow { sales_order_id: string; product_id: string; variant_id: string | null; qty_ordered: number; qty_fulfilled: number; qty_allocated: number; }
interface PoRow { id: string; po_number: string; status: string; expected_date: string; total: number; suppliers?: { name: string } | null; }
interface SoRow { id: string; order_number: string; status: string; total: number; created_at: string; customers?: { name: string } | null; }
interface TransferRow { id: string; transfer_number: string; from_location_id: string; to_location_id: string; status: string; }
interface ReturnRow { id: string; return_number: string; status: string; customers?: { name: string } | null; }
interface ShipmentRow { id: string; status: string; tracking_number: string | null; fulfilments?: { sales_order_id: string } | null; }
interface LocationRow { id: string; name: string; }
interface LedgerRow { product_id: string; qty_change: number; }
interface ConnectorRow { id: string; name: string; }
interface ExceptionRow { id: string; connector_id: string | null; exception_type: string; entity_ref: string | null; status: string; created_at: string; }
interface AutomationLogRow { id: string; event_type: string; error_message: string | null; execution_result: string | null; created_at: string; }

type Severity = "critical" | "warning";

interface AttentionItem {
  key: string;
  severity: Severity;
  title: string;
  detail: string;
  count: number;
  path: string;
}

interface SafeQueryResult<T> {
  data: T[];
  count: number;
  error: string | null;
}

async function safeQuery<T>(
  label: string,
  build: () => PromiseLike<{ data: unknown; error: Error | null; count?: number | null }>,
  attempts = 3,
): Promise<SafeQueryResult<T>> {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const res = await build();
      if (res.error) {
        if (isTransientError(res.error) && attempt < attempts) {
          await new Promise((r) => setTimeout(r, 250 * attempt));
          continue;
        }
        console.error(`[Dashboard] ${label} failed: ${serializeError(res.error)}`);
        return { data: [] as T[], count: res.count ?? 0, error: label };
      }
      return { data: (res.data ?? []) as T[], count: res.count ?? 0, error: null };
    } catch (e) {
      if (attempt < attempts) {
        await new Promise((r) => setTimeout(r, 250 * attempt));
        continue;
      }
      console.error(`[Dashboard] ${label} threw: ${serializeError(e)}`);
      return { data: [] as T[], count: 0, error: label };
    }
  }
  return { data: [] as T[], count: 0, error: label };
}

// Role-aware ordering. Within the same severity band, surface what THIS role
// cares about first. Roles not listed (owner/admin/manager/readonly) keep the
// default severity + count ordering.
const ROLE_PRIORITY: Record<string, string[]> = {
  warehouse: ["to-fulfil", "ship-ex", "returns", "low-stock", "blocked", "overdue-po"],
  purchasing: ["overdue-po", "low-stock", "to-fulfil", "blocked", "returns", "ship-ex"],
  sales: ["returns", "blocked", "to-fulfil", "overdue-po", "low-stock", "ship-ex"],
  operations: ["blocked", "to-fulfil", "returns", "overdue-po", "low-stock", "ship-ex"],
};

const OPERATIONS_PRIORITY: Record<string, string[]> = {
  warehouse: ["to-fulfil", "ready-ship", "in-transit", "returns", "incoming-po"],
  purchasing: ["incoming-po", "to-fulfil", "returns", "in-transit", "ready-ship"],
  sales: ["to-fulfil", "returns", "ready-ship", "incoming-po", "in-transit"],
  operations: ["to-fulfil", "returns", "incoming-po", "ready-ship", "in-transit"],
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { org, role, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleMsg, setSampleMsg] = useState("");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(() => localStorage.getItem("commerceops.setup.dismissed") === "1");

  // Role-aware quick actions: the few things an operator most often needs to do
  // next, surfaced right on the front page instead of behind a dropdown.
  const quickActions = useMemo(() => {
    const actions: { label: string; icon: string; path: string }[] = [];
    if (canWorkSales(role)) actions.push({ label: "New order", icon: "ri-file-list-line", path: "/sales-orders?new=1" });
    if (canManagePurchaseOrders(role)) actions.push({ label: "New purchase order", icon: "ri-shopping-cart-line", path: "/purchase-orders?new=1" });
    if (canOperateInventory(role)) {
      actions.push({ label: "Receive stock", icon: "ri-truck-line", path: "/purchase-orders" });
      actions.push({ label: "Pick & pack", icon: "ri-scan-2-line", path: "/fulfilment?tab=picking" });
    }
    if (canManageProducts(role)) actions.push({ label: "Add product", icon: "ri-archive-2-line", path: "/products/new" });
    return actions;
  }, [role]);

  const dashboardEnabled = dashboardQueryEnabled(authLoading, org?.id);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dashboard", org?.id],
    enabled: dashboardEnabled,
    queryFn: async () => {
      const orgId = org?.id;
      if (!orgId) {
        return {
          products: [], variants: [], balances: [], pos: [], sos: [], transfers: [], returns: [], shipments: [],
          locations: [], ledger: [], suppliers: [], soItems: [], customersCount: 0, memberCount: 0,
          pendingInvites: 0, sampleDataLoaded: false, connectors: [], exceptions: [], automationLogs: [], anomalyCount: 0,
          partialErrors: ["No organization found"],
        };
      }
      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

        const [products, variants, balances, pos, sos, transfers, returns, shipments, locations, ledger, suppliers, soItems, customersCount, memberships, invitations, orgSettings, connectors, exceptions, automationLogs, anomalies] = await Promise.all([
          safeQuery<ProductRow>("products", () => supabase.from("products").select("id, name, sku, status, reorder_point, reorder_qty, cost_price").eq("org_id", org!.id)),
          safeQuery<VariantRow>("variants", () => supabase.from("variants").select("id, product_id, cost_price").eq("org_id", org!.id)),
          safeQuery<BalanceRow>("balances", async () => {
            const res = await supabase.from("inventory_balances").select("product_id, variant_id, location_id, on_hand, allocated, available, incoming, products(name, reorder_point, reorder_qty), locations(name)").eq("org_id", org!.id);
            return {
              data: (res.data ?? []).map((row) => ({ ...row, products: firstRelation(row.products), locations: firstRelation(row.locations) })),
              error: res.error,
              count: res.count,
            };
          }),
          safeQuery<PoRow>("pos", async () => {
            const res = await supabase.from("purchase_orders").select("id, po_number, status, expected_date, total, suppliers(name)").eq("org_id", org!.id).order("created_at", { ascending: false });
            return { data: (res.data ?? []).map((row) => ({ ...row, suppliers: firstRelation(row.suppliers) })), error: res.error, count: res.count };
          }),
          safeQuery<SoRow>("sos", async () => {
            const res = await supabase.from("sales_orders").select("id, order_number, status, total, created_at, customers(name)").eq("org_id", org!.id).order("created_at", { ascending: false });
            return { data: (res.data ?? []).map((row) => ({ ...row, customers: firstRelation(row.customers) })), error: res.error, count: res.count };
          }),
          safeQuery<TransferRow>("transfers", () => supabase.from("stock_transfers").select("id, transfer_number, from_location_id, to_location_id, status").eq("org_id", org!.id).order("created_at", { ascending: false })),
          safeQuery<ReturnRow>("returns", async () => {
            const res = await supabase.from("sales_returns").select("id, return_number, status, customers(name)").eq("org_id", org!.id).order("created_at", { ascending: false });
            return { data: (res.data ?? []).map((row) => ({ ...row, customers: firstRelation(row.customers) })), error: res.error, count: res.count };
          }),
          safeQuery<ShipmentRow>("shipments", async () => {
            const res = await supabase.from("shipments").select("id, status, tracking_number, fulfilments(sales_order_id)").eq("org_id", org!.id).order("created_at", { ascending: false });
            return { data: (res.data ?? []).map((row) => ({ ...row, fulfilments: firstRelation(row.fulfilments) })), error: res.error, count: res.count };
          }),
          safeQuery<LocationRow>("locations", () => supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name")),
          safeQuery<LedgerRow>("ledger", () => supabase.from("inventory_ledger").select("product_id, qty_change").eq("org_id", org!.id).eq("entry_type", "sale_fulfilment").gte("created_at", thirtyDaysAgo)),
          safeQuery<{ id: string; name: string }>("suppliers", () => supabase.from("suppliers").select("id, name").eq("org_id", org!.id).order("name")),
          safeQuery<SoItemRow>("soItems", async () => {
            const res = await supabase.from("sales_order_items").select("sales_order_id, product_id, variant_id, qty_ordered, qty_fulfilled, qty_allocated").eq("org_id", org!.id);
            return { data: res.data ?? [], error: res.error, count: res.count };
          }),
          safeQuery<{ id: string }>("customers", () => supabase.from("customers").select("id", { count: "exact", head: true }).eq("org_id", org!.id)),
          safeQuery<{ id: string }>("memberships", () => supabase.from("memberships").select("id", { count: "exact", head: true }).eq("org_id", org!.id)),
          safeQuery<{ id: string }>("invitations", () => supabase.from("invitations").select("id", { count: "exact", head: true }).eq("org_id", org!.id).eq("status", "pending")),
          safeQuery<{ settings?: { sample_data_loaded?: boolean } }>("orgSettings", async () => {
            const res = await supabase.from("organizations").select("settings").eq("id", org!.id).maybeSingle();
            return { data: res.data ? { settings: res.data.settings } : null, error: res.error, count: res.count };
          }),
          safeQuery<ConnectorRow>("connectors", () => supabase.from("connectors").select("id, name").eq("org_id", org!.id)),
          safeQuery<ExceptionRow>("exceptions", () => supabase.from("integration_exceptions").select("id, connector_id, exception_type, entity_ref, status, created_at").eq("org_id", org!.id).order("created_at", { ascending: false }).limit(20)),
          safeQuery<AutomationLogRow>("automationLogs", () => supabase.from("automation_rule_logs").select("id, event_type, error_message, execution_result, created_at").eq("org_id", org!.id).order("created_at", { ascending: false }).limit(20)),
          safeQuery<{ id: string }>("anomalies", () => supabase.from("operational_anomalies").select("id", { count: "exact", head: true }).eq("org_id", org!.id).in("status", ["open", "acknowledged"])),
        ]);

        const errors: string[] = [];
        if (products.error) errors.push(products.error);
        if (variants.error) errors.push(variants.error);
        if (balances.error) errors.push(balances.error);
        if (pos.error) errors.push(pos.error);
        if (sos.error) errors.push(sos.error);
        if (transfers.error) errors.push(transfers.error);
        if (returns.error) errors.push(returns.error);
        if (shipments.error) errors.push(shipments.error);
        if (locations.error) errors.push(locations.error);
        if (ledger.error) errors.push(ledger.error);
        if (suppliers.error) errors.push(suppliers.error);
        if (soItems.error) errors.push(soItems.error);
        if (customersCount.error) errors.push(customersCount.error);
        if (memberships.error) errors.push(memberships.error);
        if (invitations.error) errors.push(invitations.error);
        if (orgSettings.error) errors.push(orgSettings.error);
        if (connectors.error) errors.push(connectors.error);
        if (exceptions.error) errors.push(exceptions.error);
        if (automationLogs.error) errors.push(automationLogs.error);
        if (anomalies.error) errors.push(anomalies.error);

        return {
          products: products.data,
          variants: variants.data,
          balances: balances.data,
          pos: pos.data,
          sos: sos.data,
          transfers: transfers.data,
          returns: returns.data,
          shipments: shipments.data,
          locations: locations.data,
          ledger: ledger.data,
          suppliers: suppliers.data,
          soItems: soItems.data,
          customersCount: customersCount.count,
          memberCount: memberships.count,
          pendingInvites: invitations.count,
          sampleDataLoaded: !!(orgSettings.data?.[0]?.settings?.sample_data_loaded),
          connectors: connectors.data,
          exceptions: exceptions.data,
          automationLogs: automationLogs.data,
          anomalyCount: anomalies.count,
          partialErrors: errors,
        };
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[Dashboard] queryFn fatal error:", e);
        return {
          products: [], variants: [], balances: [], pos: [], sos: [], transfers: [], returns: [], shipments: [], locations: [], ledger: [], suppliers: [], soItems: [], customersCount: 0, memberCount: 0, pendingInvites: 0, sampleDataLoaded: false, connectors: [], exceptions: [], automationLogs: [], anomalyCount: 0, partialErrors: [`Fatal: ${msg}`],
        };
      }
    },
  });

  const computed = useMemo(() => {
    if (!data) return null;
    const { products, variants, balances, pos, sos, transfers, returns, shipments, locations, ledger, suppliers, soItems, customersCount, memberCount, pendingInvites, connectors, exceptions, automationLogs, anomalyCount } = data;

    const availableByProduct: Record<string, number> = {};
    const incomingByProduct: Record<string, number> = {};
    const balKey = (pid: string, vid: string | null) => `${pid}::${vid ?? ""}`;
    const availableByPV: Record<string, number> = {};
    for (const b of balances) {
      availableByProduct[b.product_id] = (availableByProduct[b.product_id] ?? 0) + Number(b.available ?? 0);
      incomingByProduct[b.product_id] = (incomingByProduct[b.product_id] ?? 0) + Number(b.incoming ?? 0);
      availableByPV[balKey(b.product_id, b.variant_id)] = (availableByPV[balKey(b.product_id, b.variant_id)] ?? 0) + Number(b.available ?? 0);
    }

    const velocity: Record<string, number> = {};
    for (const l of ledger) {
      velocity[l.product_id] = (velocity[l.product_id] ?? 0) + Math.abs(Number(l.qty_change ?? 0));
    }

    const lowStock = products
      .filter((p) => p.status !== "archived" && (availableByProduct[p.id] ?? 0) <= Number(p.reorder_point ?? 0))
      .map((p) => {
        const available = availableByProduct[p.id] ?? 0;
        const dailyDemand = (velocity[p.id] ?? 0) / 30;
        const daysOfStock = dailyDemand > 0 ? available / dailyDemand : available > 0 ? Number.POSITIVE_INFINITY : 0;
        return { ...p, available, incoming: incomingByProduct[p.id] ?? 0, daysOfStock };
      })
      .sort((a, b) => {
        if (a.available === 0 && b.available !== 0) return -1;
        if (b.available === 0 && a.available !== 0) return 1;
        if (a.daysOfStock !== b.daysOfStock) return a.daysOfStock - b.daysOfStock;
        return a.name.localeCompare(b.name);
      });

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayIso = todayStart.toISOString();

    const incomingPOs = pos.filter((p) => ["pending", "partial"].includes(p.status));
    const overduePOs = incomingPOs.filter((p) => p.expected_date && p.expected_date < todayIso.slice(0, 10));

    const toFulfil = sos.filter((s) => ["confirmed", "allocated", "partially_allocated", "ready_to_pick", "partially_fulfilled", "partial"].includes(s.status));

    const toFulfilIds = new Set(toFulfil.map((s) => s.id));
    const itemsBySo: Record<string, SoItemRow[]> = {};
    for (const it of soItems) {
      if (!toFulfilIds.has(it.sales_order_id)) continue;
      (itemsBySo[it.sales_order_id] ??= []).push(it);
    }
    const blockedOrders = toFulfil.filter((so) => {
      const items = itemsBySo[so.id] ?? [];
      return items.some((it) => {
        const remaining = Number(it.qty_ordered ?? 0) - Number(it.qty_fulfilled ?? 0);
        const coverable = Number(it.qty_allocated ?? 0) + (availableByPV[balKey(it.product_id, it.variant_id)] ?? 0);
        return remaining > coverable;
      });
    });

    const shippedSoIds = new Set(
      shipments.map((s) => s.fulfilments?.sales_order_id).filter(Boolean) as string[],
    );
    const readyToShip = sos.filter((s) => s.status === "fulfilled" && !shippedSoIds.has(s.id));

    const inTransitShipments = shipments.filter((s) => s.status === "shipped");
    const inTransitTransfers = transfers.filter((t) => t.status === "in_transit");
    const draftTransfers = transfers.filter((t) => t.status === "draft");
    const shipmentExceptions = shipments.filter((s) => s.status === "exception");

    const returnsToAction = returns.filter((r) => ["draft", "approved", "received"].includes(r.status));

    const openExceptions = exceptions.filter((e) => e.status !== "resolved" && e.status !== "ignored");
    const failedAutomations = automationLogs.filter((l) => !!l.error_message || l.execution_result === "failed");

    const outOfStockCount = lowStock.filter((p) => p.available === 0).length;

    // --- Single ordered attention list (Problems first, then work) ---
    const attention: AttentionItem[] = [];
    if (blockedOrders.length > 0) {
      attention.push({ key: "blocked", severity: "critical", title: `${blockedOrders.length} ${blockedOrders.length === 1 ? "order" : "orders"} blocked by stock`, detail: "Not enough stock to fulfil", count: blockedOrders.length, path: "/sales-orders" });
    }
    if (shipmentExceptions.length > 0) {
      attention.push({ key: "ship-ex", severity: "critical", title: `${shipmentExceptions.length} ${shipmentExceptions.length === 1 ? "shipment needs" : "shipments need"} investigation`, detail: "Carrier or dispatch problems", count: shipmentExceptions.length, path: "/fulfilment?tab=shipments" });
    }
    if (isAdmin(role) && openExceptions.length > 0) {
      attention.push({ key: "connector", severity: "critical", title: `${openExceptions.length} connector ${openExceptions.length === 1 ? "error" : "errors"}`, detail: "Marketplace or integration issues", count: openExceptions.length, path: "/integrations/exceptions" });
    }
    if (isAdmin(role) && failedAutomations.length > 0) {
      attention.push({ key: "automation", severity: "critical", title: `${failedAutomations.length} automation ${failedAutomations.length === 1 ? "failure" : "failures"}`, detail: "Rules did not complete", count: failedAutomations.length, path: "/automation" });
    }
    if (anomalyCount > 0) {
      attention.push({ key: "anomalies", severity: "warning", title: `${anomalyCount} operational ${anomalyCount === 1 ? "exception needs" : "exceptions need"} review`, detail: "Anomalies and dead letters detected", count: anomalyCount, path: "/exceptions" });
    }
    if (lowStock.length > 0) {
      attention.push({ key: "low-stock", severity: "warning", title: `${lowStock.length} ${lowStock.length === 1 ? "SKU is" : "SKUs are"} below reorder point`, detail: outOfStockCount > 0 ? `${outOfStockCount} out of stock` : "Review and reorder", count: lowStock.length, path: "/replenishment" });
    }
    if (overduePOs.length > 0) {
      attention.push({ key: "overdue-po", severity: "warning", title: `${overduePOs.length} purchase ${overduePOs.length === 1 ? "order" : "orders"} overdue`, detail: "Past expected delivery date", count: overduePOs.length, path: "/purchase-orders" });
    }
    if (returnsToAction.length > 0) {
      attention.push({ key: "returns", severity: "warning", title: `${returnsToAction.length} ${returnsToAction.length === 1 ? "return awaits" : "returns await"} action`, detail: "Inspection or refund needed", count: returnsToAction.length, path: "/returns" });
    }
    if (toFulfil.length > 0) {
      attention.push({ key: "to-fulfil", severity: "warning", title: `${toFulfil.length} ${toFulfil.length === 1 ? "order" : "orders"} awaiting fulfilment`, detail: "Pick, pack and ship", count: toFulfil.length, path: "/sales-orders" });
    }

    const severityRank: Record<Severity, number> = { critical: 0, warning: 1 };
    const roleOrder = ROLE_PRIORITY[role ?? ""] ?? [];
    const roleRank = (key: string) => {
      const idx = roleOrder.indexOf(key);
      return idx === -1 ? roleOrder.length : idx;
    };
    attention.sort((a, b) => severityRank[a.severity] - severityRank[b.severity] || roleRank(a.key) - roleRank(b.key) || b.count - a.count);

    // --- Today's operations (compact, clickable) ---
    const operations = [
      { key: "to-fulfil", label: "To fulfil", count: toFulfil.length, path: "/sales-orders" },
      { key: "ready-ship", label: "Ready to ship", count: readyToShip.length, path: "/fulfilment?tab=shipments" },
      { key: "incoming-po", label: "Incoming POs", count: incomingPOs.length, path: "/purchase-orders" },
      { key: "returns", label: "Returns to action", count: returnsToAction.length, path: "/returns" },
      { key: "in-transit", label: "In transit", count: inTransitShipments.length + inTransitTransfers.length + draftTransfers.length, path: "/fulfilment?tab=shipments" },
    ].sort((a, b) => {
      const order = OPERATIONS_PRIORITY[role ?? ""];
      if (!order) return 0;
      const rank = (k: string) => {
        const i = order.indexOf(k);
        return i === -1 ? order.length : i;
      };
      return rank(a.key) - rank(b.key);
    });

    // --- Business snapshot (restrained secondary) ---
    const ordersToday = sos.filter((s) => s.created_at >= todayIso).length;
    const revenueToday = sos.filter((s) => s.created_at >= todayIso && s.status !== "cancelled").reduce((sum, s) => sum + Number(s.total ?? 0), 0);
    const totalProducts = products.filter((p) => p.status !== "archived").length;
    const stockValuation = buildStockValuation({
      products,
      variants,
      balances,
      locations,
    });
    const stockCard = dashboardStockValueCard(stockValuation.summary, money);

    // Role-aware snapshot: surface only the KPIs this role actually cares about.
    const roleName = role ?? "";
    type SnapshotTile = { label: string; value: string; hint?: string; hintWarning?: boolean; path: string };
    let snapshot: SnapshotTile[];
    if (roleName === "warehouse" || roleName === "operations") {
      snapshot = [
        { label: "To fulfil", value: String(toFulfil.length), path: "/sales-orders" },
        { label: "Ready to ship", value: String(readyToShip.length), path: "/fulfilment?tab=shipments" },
        { label: "Low stock", value: String(lowStock.length), path: "/replenishment" },
        { label: "Active products", value: String(totalProducts), path: "/products" },
      ];
    } else if (roleName === "purchasing") {
      snapshot = [
        { label: "Open purchase orders", value: String(incomingPOs.length), path: "/purchase-orders" },
        { label: "Overdue", value: String(overduePOs.length), path: "/purchase-orders" },
        { label: "Low stock", value: String(lowStock.length), path: "/replenishment" },
        { label: "Stock value (at cost)", value: stockCard.value, hint: stockCard.hint, hintWarning: stockCard.completeness === "partial", path: "/reports" },
      ];
    } else {
      snapshot = [
        { label: "Orders today", value: String(ordersToday), path: "/sales-orders" },
        { label: "Revenue today", value: money(revenueToday), path: "/sales-orders" },
        { label: "Active products", value: String(totalProducts), path: "/products" },
        { label: "Stock value (at cost)", value: stockCard.value, hint: stockCard.hint, hintWarning: stockCard.completeness === "partial", path: "/reports" },
      ];
    }

    return {
      snapshot,
      products,
      lowStock,
      attention,
      operations,
      ordersToday,
      revenueToday,
      totalProducts,
      stockValue: stockValuation.summary.inventoryValueAtCost,
      sampleDataLoaded: data.sampleDataLoaded,
      setupSteps: [
        { label: "Add your products", done: totalProducts > 0, path: "/products", icon: "ri-archive-2-line" },
        { label: "Set up a location", done: locations.length > 0, path: "/locations", icon: "ri-map-pin-line" },
        { label: "Add your suppliers", done: suppliers.length > 0, path: "/suppliers", icon: "ri-truck-line" },
        { label: "Add your customers", done: customersCount > 0, path: "/customers", icon: "ri-user-3-line" },
        { label: "Invite your team", done: memberCount > 1 || pendingInvites > 0, path: "/settings/users", icon: "ri-team-line" },
        { label: "Establish opening stock", done: balances.length > 0, path: "/inventory", icon: "ri-inbox-line" },
      ],
      connectorName: connectors.reduce<Record<string, string>>((acc, c) => { acc[c.id] = c.name; return acc; }, {}),
      partialErrors: data.partialErrors ?? [],
    };
  }, [data, role]);

  const loadSample = async () => {
    setSampleLoading(true);
    setSampleMsg("");
    const { error } = await supabase.rpc("load_sample_data", { p_org_id: org!.id });
    setSampleLoading(false);
    if (error) setSampleMsg(error.message);
    else {
      setSampleMsg("Sample data loaded.");
      queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    }
  };

  const removeSample = async () => {
    setSampleLoading(true);
    setSampleMsg("");
    const { error } = await supabase.rpc("remove_sample_data", { p_org_id: org!.id });
    setSampleLoading(false);
    setRemoveOpen(false);
    if (error) setSampleMsg(error.message);
    else {
      setSampleMsg("Sample data removed.");
      queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    }
  };

  const dismissSetup = () => {
    localStorage.setItem("commerceops.setup.dismissed", "1");
    setSetupDismissed(true);
  };

  const surface = dashboardWorkspaceSurface({
    authLoading,
    orgId: org?.id,
    queryLoading: isLoading,
    queryEnabled: dashboardEnabled,
    hasComputed: !!computed,
    isError,
  });

  if (surface === "loading") return <LoadingState />;
  if (surface === "error" || !computed) {
    const errorMsg = error instanceof Error ? error.message : isError ? "Unknown error" : "Dashboard data is unavailable";
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
        <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-red-500 text-xl" />
        </div>
        <h3 className="text-base font-semibold text-slate-800 mb-1">Couldn't load dashboard</h3>
        <p className="text-sm text-slate-500 mb-4">Something went wrong fetching your data.</p>
        {errorMsg && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-100 rounded text-xs text-red-700 font-mono break-all max-w-md mx-auto">
            {errorMsg}
          </div>
        )}
        <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-md whitespace-nowrap">
          Retry
        </button>
      </div>
    );
  }

  const c = computed;
  const severityDot = (sev: Severity) => (sev === "critical" ? "bg-red-500" : "bg-amber-500");

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Operations</h1>
        <p className="text-sm text-slate-500 mt-1">What needs your attention today</p>
      </div>

      {quickActions.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {quickActions.map((a) => (
            <button
              key={a.label}
              onClick={() => navigate(a.path)}
              className="px-4 py-2.5 bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className={`${a.icon} text-emerald-600`} aria-hidden="true" />
              {a.label}
            </button>
          ))}
        </div>
      )}

      {c.partialErrors.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <i className="ri-alert-line text-amber-600 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">Some data couldn't load</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {c.partialErrors.map(describeDashboardError).join(", ")} — counts and summaries may be incomplete.
            </p>
          </div>
          <button onClick={() => refetch()} className="text-xs font-medium text-amber-800 hover:text-amber-900 whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      {/* Getting started (only while core setup is incomplete) */}
      {!setupDismissed && c.setupSteps.some((s) => !s.done) && (
        <div className="mb-6 bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Getting started</h2>
              <p className="text-xs text-slate-500 mt-0.5">A few quick steps to set up your business and start using the platform.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                {c.setupSteps.filter((s) => s.done).length} of {c.setupSteps.length} done
              </span>
              <button onClick={dismissSetup} className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap">Dismiss</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {c.setupSteps.map((s) => (
              <button
                key={s.label}
                onClick={() => navigate(s.path)}
                className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                  s.done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  <i className={s.icon} />
                </div>
                <span className="flex-1 text-sm font-medium text-slate-700">{s.label}</span>
                {s.done ? <i className="ri-checkbox-circle-fill text-emerald-500" /> : <i className="ri-arrow-right-s-line text-slate-400" />}
              </button>
            ))}
          </div>

          {isAdmin(role) && c.totalProducts === 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Want to explore with example data?</p>
                <p className="text-xs text-slate-500 mt-0.5">Load clearly-labelled sample products, suppliers, customers and orders to see how Tradivo works. You can remove it later.</p>
              </div>
              <button onClick={loadSample} disabled={sampleLoading} className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-md whitespace-nowrap disabled:opacity-60">
                {sampleLoading ? "Loading..." : "Load sample data"}
              </button>
            </div>
          )}

          {isAdmin(role) && c.sampleDataLoaded && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <p className="text-sm text-slate-600">Sample data is loaded in this workspace.</p>
              <button onClick={() => setRemoveOpen(true)} disabled={sampleLoading} className="px-3 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium rounded-md whitespace-nowrap disabled:opacity-60">
                Remove sample data
              </button>
            </div>
          )}

          {sampleMsg && <p className="mt-3 text-xs text-slate-600">{sampleMsg}</p>}
        </div>
      )}

      {/* PRIORITY 1 — Needs attention */}
      <section className="mb-6 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Needs attention</h2>
          {c.attention.length > 0 && (
            <span className="text-xs font-medium text-slate-500">{c.attention.length} item{c.attention.length === 1 ? "" : "s"}</span>
          )}
        </div>
        {c.attention.length === 0 ? (
          attentionDegraded(c.partialErrors) ? (
            <div className="px-6 py-12 text-center">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                <i className="ri-alert-line text-amber-500" />
              </div>
              <p className="text-sm font-medium text-slate-800">Can't confirm everything is fine</p>
              <p className="text-xs text-slate-500 mt-1">Some data couldn't load, so attention items may be missing.</p>
              <button onClick={() => refetch()} className="mt-3 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-md whitespace-nowrap">Retry</button>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-line text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-slate-800">Nothing needs your attention</p>
              <p className="text-xs text-slate-500 mt-1">All operations are running smoothly.</p>
            </div>
          )
        ) : (
          <div className="divide-y divide-slate-50">
            {c.attention.map((item) => (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${severityDot(item.severity)}`} aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-slate-800">{item.title}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{item.detail}</span>
                </span>
                <span className="text-xs font-medium text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                  View <i className="ri-arrow-right-line" aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* PRIORITY 2 — Today's operations */}
      <section className="mb-6 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">Today's operations</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5">
          {c.operations.map((op) => (
            <button
              key={op.label}
              onClick={() => navigate(op.path)}
              className="px-5 py-4 text-left hover:bg-slate-50 transition-colors border-slate-50 border-l first:border-l-0"
            >
              <p className="text-xl font-semibold text-slate-900 leading-none">{op.count}</p>
              <p className="text-xs text-slate-500 mt-1.5">{op.label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* PRIORITY 3 — Business snapshot */}
      <section className="mb-6 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">Snapshot</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4">
          {c.snapshot.map((s) => (
            <button key={s.label} onClick={() => navigate(s.path)} className="px-5 py-4 text-left border-l border-slate-50 first:border-l-0 hover:bg-slate-50 transition-colors cursor-pointer">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className="text-lg font-semibold text-slate-900 mt-1">{s.value}</p>
              {s.hint && (
                <p className={`text-[11px] mt-1 ${s.hintWarning ? "text-amber-700" : "text-slate-400"}`}>{s.hint}</p>
              )}
            </button>
          ))}
        </div>
      </section>

      <ConfirmDialog
        open={removeOpen}
        title="Remove sample data"
        message="This removes all products, suppliers, customers, orders, inventory and related records from this workspace. This cannot be undone."
        confirmLabel="Remove data"
        loading={sampleLoading}
        onCancel={() => setRemoveOpen(false)}
        onConfirm={removeSample}
      />
    </div>
  );
}
